import { type Cell, type Sheet, usedExtent } from "@package/scratchpad-utils";

export type CellPatch = {
  sheetId: string;
  key: string;
  before: string | null;
  after: string | null;
};

export function diffSheetCells(prev: Sheet[], next: Sheet[]): CellPatch[] {
  const patches: CellPatch[] = [];
  const nextById = new Map(next.map((s) => [s.id, s]));
  for (const sheet of prev) {
    const nextSheet = nextById.get(sheet.id);
    for (const [key, cell] of Object.entries(sheet.cells)) {
      const after = nextSheet?.cells[key]?.src ?? null;
      if (after !== cell.src) patches.push({ sheetId: sheet.id, key, before: cell.src, after });
    }
  }
  const prevById = new Map(prev.map((s) => [s.id, s]));
  for (const sheet of next) {
    const prevSheet = prevById.get(sheet.id);
    for (const [key, cell] of Object.entries(sheet.cells)) {
      if (prevSheet?.cells[key] === undefined) {
        patches.push({ sheetId: sheet.id, key, before: null, after: cell.src });
      }
    }
  }
  return patches;
}

export type PatchResult = {
  sheets: Sheet[];
  removals: string[];
  updates: Array<{ nodeId: string; src: string }>;
};

export function applyCellPatches(sheets: Sheet[], patches: CellPatch[], direction: "undo" | "redo"): PatchResult {
  const ordered = direction === "undo" ? [...patches].reverse() : patches;
  const removals: string[] = [];
  const updates: Array<{ nodeId: string; src: string }> = [];
  const nextCells = new Map<string, Record<string, Cell>>();
  const byId = new Map(sheets.map((s) => [s.id, s]));
  for (const patch of ordered) {
    const sheet = byId.get(patch.sheetId);
    if (!sheet) continue;
    let cells = nextCells.get(patch.sheetId);
    if (!cells) {
      cells = { ...sheet.cells };
      nextCells.set(patch.sheetId, cells);
    }
    const src = direction === "undo" ? patch.before : patch.after;
    const nodeId = `${patch.sheetId}!${patch.key}`;
    if (src === null) {
      delete cells[patch.key];
      removals.push(nodeId);
    } else {
      const existing = cells[patch.key];
      cells[patch.key] = existing ? { ...existing, src } : { src, value: null, error: null };
      updates.push({ nodeId, src });
    }
  }
  if (nextCells.size === 0) return { sheets, removals, updates };
  const next = sheets.map((sheet) => {
    const cells = nextCells.get(sheet.id);
    return cells ? { ...sheet, cells, used: usedExtent(cells) } : sheet;
  });
  return { sheets: next, removals, updates };
}
